<template>
  <wgu-app-tpl>
    <!-- Navbar  -->
    <Slider
      v-if="sliderVisible"
      slot="wgu-before-content"
      :referenceTimeLayerId="referenceLid"
      :downloadActivated=true
      :autoPlayActivated=true
      :sharedTimeStamp="sharedTimeStamp"
      :color="color"
      :locale="locale"
      :referenceTimeRange="referenceTimeRange"
      :referenceTimeFrequency="referenceTimeFrequency"
      @sliderHasChanged="updateSharedTimeStamp"
      :autoPlayIntervallMilliSeconds=1000
    />

  </wgu-app-tpl>
</template>

<script>
import Vue from 'vue'
import WguAppTemplate from './WguAppTemplate';
import DatePicker from './components/datepicker/DatePicker';
import { Mapable } from '../src/mixins/Mapable';
import LayerUtil from '../src/util/Layer';
import TimeDisplay from './components/datepicker/TimeDisplay';
import TimePicker from './components/timepicker/TimePicker'
import Slider from './components/slider/Slider';
import UrlUtil from '../src/util/Url';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector'
import TileWmsSource from 'ol/source/TileWMS';
import { TimeStampHelper } from './util/TimeStamp';
import { FilterHelper } from './util/OgcFilter';
import { OwsCapsHelper } from './util/OwsCapabilities';
import WMSCapabilities from 'ol/format/WMSCapabilities';

export default {
  name: 'my-wgu-app',
  mixins: [Mapable],

  components: {
    'wgu-app-tpl': WguAppTemplate,
    DatePicker,
    TimeDisplay,
    TimePicker,
    Slider
  },
  data () {
    return {
      show: this.getComponentShowParameter(),
      /** the slider becomes visible after layer capabilities have loaded */
      sliderVisible: false,
      sharedTimeStamp: null,
      locale: 'de-de',
      color: Vue.prototype.$appConfig.baseColor,
      /** timestamp key in the url */
      timestampUrlParam: 'time',
      /** necessary for creating a clean browser history */
      shouldUpdate: false,
      referenceLid: this.getTimeReferenceLid(),
      referenceTimeRange: null,
      referenceTimeFrequency: null,

      /** default time values if no values are provided by the appConfig */
      defaultTimeRange: '2010/2020',
      defaultTimefrequency: 'PT1H'
    }
  },
  created () {
    // get URL param 'time' and use as initial shared timestamp
    // if empty the current date is used
    const time = UrlUtil.getQueryParam(this.timestampUrlParam);
    this.sharedTimeStamp = TimeStampHelper.initSharedTimestamp(time);

    // restore time when navigating through browser history
    // TODO: browser history works fine for days but not for hours
    //       for hours clicking back twice is necessary
    window.addEventListener('popstate', (event) => {
      if (event.state === null) {
        return;
      }
      this.shouldUpdate = false;

      const state = event.state;
      this.sharedTimeStamp = new Date(state.utcTimestamp)
    });
  },
  watch: {
    sharedTimeStamp () {
      this.updateTimeStampInUrl();
      this.modifyTimeEnabledLayers();
    },
    show () {
      this.sliderVisible = this.show.slider;
    }
  },
  methods: {

    /**
     * Sets new timestamp to main module
     */
    updateSharedTimeStamp (newTimeStamp) {
      this.sharedTimeStamp = new Date(newTimeStamp);
    },

    /**
     * Switch components on and off
     */
    toggleSelect (component) {
      this.show = {
        ...this.show,
        [component]: !this.show[component]
      };
    },

    /**
     * Automatically called, once Map is bound.
     */
    onMapBound () {
      // assign min and max timestamp to layer
      const layers = this.map.getLayers();
      layers.forEach(layer => {
        const timeRange = layer.get('timeRange');
        if (!timeRange) {
          return;
        }
        const split = timeRange.split('/');
        if (split.length !== 2) {
          return;
        }
        layer.set('minTimeStamp', split[0]);
        layer.set('maxTimeStamp', split[1]);
      });

      this.readWmsCapabilities(
        this.initSlider
      );
    },

    /**
     * Activate slider with time values retrieved from
     * reference layer if available. Otherwise we use default
     * values.
     */
    initSlider () {
      const timeValues = this.getTimeValuesFromReferenceLayer();

      const range = timeValues[0];
      const frequency = timeValues[1];

      // set extracted values or defaults
      this.referenceTimeFrequency = frequency || this.defaultTimefrequency || 'PT1H';
      this.referenceTimeRange = range || this.defaultTimeRange || '2010/2020';

      // show slider
      this.sliderVisible = this.show.slider;
    },

    /**
     * Read the config parameter for the time components
     *
     * @returns {Object} Object if time components should be shown.
     */
    getComponentShowParameter () {
      let slider = false;
      let date = false;
      let clock = false;

      const appConf = Vue.prototype.$appConfig;
      if (appConf.modules && appConf.modules['wgu-time-ui']) {
        const timeUi = appConf.modules['wgu-time-ui'];
        slider = timeUi.slider;
        date = timeUi.date;
        clock = timeUi.clock;
      }

      return {
        slider: slider,
        date: date,
        clock: clock
      }
    },

    /**
     * Read range and frequency from reference layer.
     *
     * @returns [String, String] Range and frequency
     */
    getTimeValuesFromReferenceLayer () {
      if (!this.referenceLid) {
        return [undefined, undefined]
      }
      // get reference layer
      const refLayer = LayerUtil.getLayerByLid(this.referenceLid, this.map);

      let range;
      let frequency;

      // overwrite with explicit values from layer
      if (refLayer.get('timeRange')) {
        range = refLayer.get('timeRange');
      }
      if (refLayer.get('timeFrequency')) {
        frequency = refLayer.get('timeFrequency');
      }

      // overwrite with explicit values from global config
      const timeConf = Vue.prototype.$appConfig.time;
      if (timeConf && timeConf.referenceTimeRange) {
        range = timeConf.referenceTimeRange;
      }
      if (timeConf && timeConf.referenceTimeFrequency) {
        frequency = timeConf.referenceTimeFrequency;
      }

      return [range, frequency];
    },

    /**
     * Read WMS capabilities and save relevant information
     * as property to the layer.
     *
     * @param {function} onSuccess Function executed after capabilities have been read
     */
    readWmsCapabilities (onSuccess) {
      // collecting the WMS layers for the PromiseAll processing later on
      const [wmsLayers, promises] = this.requestCapabilitiesForAllWmsLayers();

      Promise.all(promises).then(
        capXmls => {
          capXmls.forEach((capXml, index) => {
            if (capXml instanceof Error) {
              console.warn(capXml)
            } else {
              // take layer and layerName from before
              const layer = wmsLayers[index].layer;
              const layerName = wmsLayers[index].layerName;

              const parser = new WMSCapabilities();
              const wmsCaps = parser.read(capXml);

              this.processWmsCapabilities(wmsCaps, layer, layerName);
            }
          });
        }
      ).then(onSuccess)
    },

    /**
     * Updates the timestamp in the url
     */
    updateTimeStampInUrl () {
      if (!this.shouldUpdate) {
        // do not update the URL when the time was changed in the 'popstate' handler
        this.shouldUpdate = true;
        return;
      }
      const isoString = this.sharedTimeStamp.toISOString();

      const state = {
        utcTimestamp: isoString
      };

      // TODO: adapt length of timestamp according to reference layer

      // build new url
      const urlObj = {};
      // remove seconds from ISO-Timestring
      const urlTimestamp = TimeStampHelper.extractYear(isoString);
      urlObj[this.timestampUrlParam] = urlTimestamp;
      let queryString = UrlUtil.toQueryString(urlObj);
      // make query string pretty
      queryString = decodeURIComponent(queryString);
      const urlHash = '?';

      // update URL
      window.history.pushState(state, '', urlHash + queryString);
    },

    /**
     * Set new timestamp to all time-enabled WMS-T layers
     */
    modifyTimeEnabledLayers () {
      const layers = LayerUtil.getLayersBy('timeEnabled', true, this.map);
      layers.forEach(layer => {
        const source = layer.getSource();
        if (!source) {
          return;
        }
        // WMS
        if (layer instanceof TileLayer && source instanceof TileWmsSource) {
          // it is important to create a new instance here,
          // otherwise we modify the sharedTimeStamp
          let timeStampForWmst = new Date(this.sharedTimeStamp);

          let timeExpectedDateDigits;
          if (layer.get('timeNeedsShortendDigits') && layer.get('minTimeStamp')) {
            // const startTime = timeRange.split('/')[0];
            const startTime = layer.get('minTimeStamp');
            timeExpectedDateDigits = startTime.length;
          }

          const formattedTimeStamp =
            TimeStampHelper.adaptTimeStampForWms(
              timeStampForWmst,
              layer.get('timeFrequency'),
              timeExpectedDateDigits
            );

          source.updateParams(
            {
              'TIME': formattedTimeStamp
            }
          );
          // WFS
        } else if (layer instanceof VectorLayer &&
                  layer.get('timeProperty') &&
                  layer.get('version')) {
          // create time filter
          const timeFilterXml = FilterHelper.createTimeFilter(
            layer.get('timeProperty'),
            this.sharedTimeStamp, // start
            this.sharedTimeStamp, // end
            layer.get('version')
          );
          if (!timeFilterXml) {
            layer.setVisible(false);
            return;
          }
          // add it as property to source
          source.set('timeFilterXml', timeFilterXml)
          source.clear();
          source.refresh();
        }
      });
    },

    /**
     * Extract referencing time layer ID from the appConfig.
     *
     * @returns {String} The layer ID of the referencing time layer.
     */
    getTimeReferenceLid () {
      let foundReferenceLid;
      // check if reference layer is explicitly given
      const appConf = Vue.prototype.$appConfig;
      if (appConf.time && appConf.time.referenceLid) {
        foundReferenceLid = appConf.time.referenceLid;
      }

      // check if there is only one time-enabled layer
      // if yes, use it as reference layer
      let countTimeLayers = 0;
      let foundTimeLayer;
      appConf.mapLayers.forEach(layer => {
        if (layer.timeEnabled) {
          countTimeLayers++
          foundTimeLayer = layer;
        }
      });
      if (countTimeLayers === 1) {
        foundReferenceLid = foundTimeLayer.lid;
      }

      const referenceLayer = appConf.mapLayers.find(
        layer => layer.lid === foundReferenceLid
      );

      if (!referenceLayer || !referenceLayer.timeEnabled) {
        console.warn('Referenced time layer does not exist, or is not time enabled.')
        foundReferenceLid = undefined;
      }

      return foundReferenceLid;
    },

    /**
     * Loops through all layers and requests the capabilities for
     * the time-enabled WMS layers.
     *
     * @returns [Object[], Promise[]] Two arrays with the information about the layers and its promises
     */
    requestCapabilitiesForAllWmsLayers () {
      const layers = LayerUtil.getLayersBy('timeEnabled', true, this.map);

      const wmsLayers = [];
      const promises = [];
      layers.forEach(layer => {
        const source = layer.getSource();
        if (!source) {
          return;
        }

        // return if all time properties are already defined in the config
        if (layer.get('timeRange') && layer.get('timeFrequency')) {
          return;
        }

        if (layer instanceof TileLayer && source instanceof TileWmsSource) {
          const wmsPromise = OwsCapsHelper.requestWmsCapabilites(source);
          promises.push(wmsPromise)

          const layersProperty = source.getParams()['LAYERS'];
          // if LAYERS is a list of layers, we return
          if (!layersProperty || layersProperty.includes(',')) {
            return;
          }

          let layerName = layersProperty;
          // if the layername also contains the workspace,
          // we have to remove the workspace and only keep
          // the name
          const split = layerName.split(':');
          if (split.length === 2) {
          // take the part after the colon
            layerName = split[1];
          }

          // remember layer and layerName for later
          wmsLayers.push(
            {
              layer: layer,
              layerName: layerName
            }
          );
        }
      })
      return [wmsLayers, promises];
    },

    /**
     * Reads the time information from the capabilities
     * and stores them as propeties to the OpenLayers layer object.
     *
     * @param {Object} wmsCapabilities The WMS capabilities
     * @param {ol.layer} layer An OpenLayers layer object
     * @param {String} layerName The name of the layer
     */
    processWmsCapabilities (wmsCaps, layer, layerName) {
      const timeDimensions =
                OwsCapsHelper.extractTimeDimensionsFromWmsCapabilities(
                  wmsCaps,
                  layerName
                );

      // only add time dimensions if valid
      if (timeDimensions && typeof timeDimensions.values === 'string') {
        const split = timeDimensions.values.split('/');
        if (split.length === 3) {
          layer.set('timeDimensions', timeDimensions);
        }
        // set min/max values of the the timerange
        if (!layer.get('minTimeStamp') || !layer.get('maxTimeStamp')) {
          layer.set('minTimeStamp', split[0]);
          layer.set('maxTimeStamp', split[1]);
        }

        // set timeFrequency to layer
        if (!layer.get('timeFrequency')) {
          layer.set('timeFrequency', split[2]);
        }

        if (!layer.get('timeRange')) {
          layer.set('timeRange', `${split[0]}/${split[1]}`);
        }
      } else {
        console.warn(`Could not read timeDimension from Capabilities of layer: '${layer.get('name')}'`);
      }
    }
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// app/WguApp.vue